import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import Api from "./utils/apiCall";
import { API_URLS } from "./apis/urls";

export interface ERequisitionItem {
  ReferenceNumber?: string;
  SerialNumber: string;
  Items: string;
  Remarks: string;
  AllotementOrNot?: string;
}

export interface ERequisitionRequest {
  id?: number | string;
  ReferenceNumber?: string;
  referenceNo?: string;
  EmpID?: string;
  EmpName?: string;
  EmployeeName?: string;
  Designation?: string;
  Department?: string;
  ContactNumber?: string;
  OfficeLocation?: string;
  EmailID?: string;
  SelectRequestFromType?: string;
  SelectRequestFromTypeText?: string;
  itFormType?: string;
  requestFormType?: string;
  AlreadyIssueITAsset?: string;
  SupervisorUnderHOD?: string;
  requestCreationDate?: string;
  RequestCreationDate?: string;
  createdAt?: string;
  created_at?: string;
  requestStatus?: string;
  RequestStatus?: string;
  status?: string;
  Status?: string;
  lastApprover?: string;
  ApproverUserID?: string;
  ApproverName?: string;
  HRemarks?: string;
  Remarks?: string;
  remarks?: string;
  is_draft?: boolean | number | string;
  List_Item?: { results: ERequisitionItem[] };
  ToList?: { results: ERequisitionItem[] };
  [key: string]: any;
}

interface ERequisitionState {
  listData: any | null;
  masterData: any | null;
  detailData: any | null;
  loading: "idle" | "pending" | "fulfilled" | "rejected";
  error: string | null;
  action: "list" | "master" | "findOne" | "createUpdate" | "approveReject" | "deleteDraft" | "sync" | null;
}

const initialState: ERequisitionState = {
  listData: null, masterData: null, detailData: null,
  loading: "idle", error: null, action: null
};

// Generic API thunk creator helper
const createApiThunk = (type: string, url: string) =>
  createAsyncThunk(`eRequisition/${type}`, async (payload: any = {}, thunkAPI) => {
    try {
      const body = await Api.post(url, payload);
      const nested = body?.data;
      const failed =
        body?.success === false ||
        body?.status === false ||
        (typeof body?.status === 'number' && body.status >= 400) ||
        nested?.success === false ||
        (typeof nested?.status === 'number' && nested.status >= 400);
      if (failed) {
        return thunkAPI.rejectWithValue(nested?.message || body?.message || nested?.error || 'Request failed');
      }
      return body;
    } catch (err: any) {
      return thunkAPI.rejectWithValue(Api.getErrorMessage(err));
    }
  });

export const fetchERequisitionList = createApiThunk("fetchList", API_URLS.E_REQUISITION.FIND_ALL);
export const findOneERequisition = createApiThunk("findOne", API_URLS.E_REQUISITION.FIND_ONE);
export const fetchERequisitionMaster = createApiThunk("master", API_URLS.E_REQUISITION.MASTER_FIND_ALL);
export const createUpdateERequisition = createApiThunk("createUpdate", API_URLS.E_REQUISITION.CREATE_UPDATE);
export const approveRejectERequisition = createApiThunk("approveReject", API_URLS.E_REQUISITION.APPROVE_REJECT);
export const deleteDraftERequisition = createApiThunk("deleteDraft", API_URLS.E_REQUISITION.DELETE_DRAFT);
export const syncERequisition = createApiThunk("sync", API_URLS.E_REQUISITION.SYNC);

const eRequisitionSlice = createSlice({
  name: "eRequisition",
  initialState,
  reducers: {
    resetERequisitionState: (state) => {
      state.loading = "idle";
      state.error = null;
      state.action = null;
    },
    clearDetailData: (state) => {
      state.detailData = null;
    },
  },
  extraReducers: (builder) => {
    const handlePending = (actionName: ERequisitionState["action"]) => (state: ERequisitionState) => {
      state.loading = "pending";
      state.error = null;
      state.action = actionName;
    };
    const handleRejected = (state: ERequisitionState, action: any) => {
      state.loading = "rejected";
      state.error = action.payload as string;
    };

    builder
      .addCase(fetchERequisitionList.pending, handlePending("list"))
      .addCase(fetchERequisitionList.fulfilled, (state, a) => { state.loading = "fulfilled"; state.listData = a.payload; })
      .addCase(fetchERequisitionList.rejected, handleRejected)

      .addCase(findOneERequisition.pending, handlePending("findOne"))
      .addCase(findOneERequisition.fulfilled, (state, a) => { state.loading = "fulfilled"; state.detailData = a.payload; })
      .addCase(findOneERequisition.rejected, handleRejected)

      .addCase(fetchERequisitionMaster.pending, handlePending("master"))
      .addCase(fetchERequisitionMaster.fulfilled, (state, a) => { state.loading = "fulfilled"; state.masterData = a.payload; })
      .addCase(fetchERequisitionMaster.rejected, handleRejected)

      .addCase(createUpdateERequisition.pending, handlePending("createUpdate"))
      .addCase(createUpdateERequisition.fulfilled, (state) => { state.loading = "fulfilled"; })
      .addCase(createUpdateERequisition.rejected, handleRejected)

      .addCase(approveRejectERequisition.pending, handlePending("approveReject"))
      .addCase(approveRejectERequisition.fulfilled, (state) => { state.loading = "fulfilled"; })
      .addCase(approveRejectERequisition.rejected, handleRejected)

      .addCase(deleteDraftERequisition.pending, handlePending("deleteDraft"))
      .addCase(deleteDraftERequisition.fulfilled, (state) => { state.loading = "fulfilled"; })
      .addCase(deleteDraftERequisition.rejected, handleRejected)

      .addCase(syncERequisition.pending, handlePending("sync"))
      .addCase(syncERequisition.fulfilled, (state) => { state.loading = "fulfilled"; })
      .addCase(syncERequisition.rejected, handleRejected);
  },
});

export const { resetERequisitionState, clearDetailData } = eRequisitionSlice.actions;
export default eRequisitionSlice.reducer;
